import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useRef } from 'react';
import type { EntityAdminClass } from '../EntityAdminClass';
import { ENTITY_INTERNAL_ROW_ID } from '../EntityAdminClass';
import type { EntityAdminRecord } from '../types';

/**
 * Hook to manage drawer sameness check logic.
 * Prevents reopening the drawer if it's already open with the same entities and mode.
 * Also clears refs when the drawer closes.
 */
export function useDrawerSamenessCheck<TRecord extends EntityAdminRecord>(isDrawerOpen: boolean) {
  const drawerEntitiesRef = useRef<EntityAdminClass<TRecord>[]>([]);
  const drawerAddingChildEntityRef = useRef<boolean>(false);

  // Clear drawer entities ref when drawer closes
  useEffect(() => {
    if (!isDrawerOpen) {
      drawerEntitiesRef.current = [];
      drawerAddingChildEntityRef.current = false;
    }
  }, [isDrawerOpen]);

  /**
   * Checks if the drawer should be opened based on sameness of entities and mode.
   * Updates refs immediately after check passes to prevent race conditions.
   * @returns true if drawer should open, false if it should be skipped
   */
  const shouldOpenDrawer = useCallback(
    (setupEntities: EntityAdminClass<TRecord>[], addingChildEntity: boolean): boolean => {
      // Check if the drawer is already open with the same entity/entities and mode by comparing their IDs and addingChildEntity
      const currentIds = drawerEntitiesRef.current.map(e => e[ENTITY_INTERNAL_ROW_ID]);
      const newIds = setupEntities.map(e => e[ENTITY_INTERNAL_ROW_ID]);
      // Only treat as same entities if both arrays have length > 0 (empty arrays shouldn't be considered "same")
      const isSameEntities = currentIds.length > 0 && newIds.length > 0 && isEqual(currentIds, newIds);
      const isSameMode = drawerAddingChildEntityRef.current === addingChildEntity;

      if (isDrawerOpen && isSameEntities && isSameMode) {
        // Don't re-open drawer if it's already open with the same entities and mode
        return false;
      }

      // Update refs immediately after check passes to prevent race conditions with rapid calls
      drawerEntitiesRef.current = setupEntities;
      drawerAddingChildEntityRef.current = addingChildEntity;
      return true;
    },
    [isDrawerOpen]
  );

  return {
    shouldOpenDrawer,
  };
}
